'use client';

import { useCallback } from 'react';

import { useTrackEvent } from '@/hooks/use-track-event';
import {
  CHAT_ANALYTICS_EVENTS,
  WIDGET_ANALYTICS_EVENTS,
  type AbortReason,
  type ChatAnalyticsEvent,
  type WidgetAnalyticsEvent,
} from '@/lib/constants/post-message-types';
import { useSyncStore } from '@/lib/sync/store';

export type LinkClickEventData = {
  url: string;
  linkText?: string;
  navigationMode: string;
  source:
    | 'markdown'
    | 'carousel'
    | 'flight-card'
    | 'vacation-package-card'
    | 'organized-trip-card'
    | 'sidebar';
  threadId?: string;
};

export type WidgetEventProperties = {
  trigger?: string;
};

export type CarouselShownEventData = {
  products: string[];
  toolName: string;
};

export type ChatEventProperties = {
  threadId?: string;
  messageCount?: number;
  streamingDurationMs?: number;
  requestDurationMs?: number;
  abortReason?: AbortReason;
  isDocumentHidden?: boolean;
  isNetworkOffline?: boolean;
  isPagePersisted?: boolean;
  hadStreamingStarted?: boolean;
  wasQueued?: boolean;
  eventTimestamp?: number;
  queuedDurationMs?: number;
};

export function useAnalytics() {
  const trackEvent = useTrackEvent();
  const activeThreadId = useSyncStore(state => state.threadId);

  const trackLinkClick = useCallback(
    (data: LinkClickEventData) => {
      const threadId = activeThreadId ?? data.threadId;
      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.LINK_CLICKED,
        properties: { ...data, ...(threadId ? { threadId } : {}) },
      });
    },
    [trackEvent, activeThreadId],
  );

  const trackWidgetEvent = useCallback(
    (event: WidgetAnalyticsEvent, properties?: WidgetEventProperties) => {
      trackEvent({ event, properties });
    },
    [trackEvent],
  );

  const trackChatEvent = useCallback(
    (event: ChatAnalyticsEvent, properties?: ChatEventProperties) => {
      trackEvent({ event, properties });
    },
    [trackEvent],
  );

  const trackCarouselShown = useCallback(
    (data: CarouselShownEventData) => {
      trackEvent({ event: CHAT_ANALYTICS_EVENTS.CAROUSEL_SHOWN, properties: data });
    },
    [trackEvent],
  );

  return {
    trackLinkClick,
    trackWidgetEvent,
    trackChatEvent,
    trackCarouselShown,
    WIDGET_ANALYTICS_EVENTS,
    CHAT_ANALYTICS_EVENTS,
  };
}
